// Passt zu den Pydantic-Modellen in app/schemas.py des Backends.

export type Artikelart = 'fertigartikel' | 'material'
/** Wie sicher sich die KI bei einer Position ist. */
export type Sicherheit = 'sicher' | 'unsicher'
/** Gewicht eines Befunds oder Hinweises. */
export type Schwere = 'hinweis' | 'warnung' | 'fehler'
/** Ein Artikel, wie ihn Listen und Pläne nennen: Nummer und Bezeichnung. */
export interface ArtikelKurz {
  nummer: string
  bezeichnung: string
}

export interface Artikel {
  id: number
  artikelnummer: string
  bezeichnung: string
  art: Artikelart
  gebindegewicht_kg: number | null
  mvn: number | null
  einheit: string
  mvn_begruendung: string | null
  gebindegruppe: string | null
  hersteller: string | null
  lieferzeit_tage: number | null
  mindestbestand_t: number | null
  /** nur Fertigartikel: geplante Monatsmenge (Forecast GF), ersetzt den gerechneten Durchschnitt */
  plan_t_je_monat: number | null
  aktiv: boolean
  /** Kopfzeile der Exporte („OTHÜNA SANNA", „Kokos-Wickler"); leer heißt: die Bezeichnung. */
  kurzname: string | null
  /** nur Material: Puffer vor der Nachauflage nur für diesen Artikel; leer = der des Lieferanten (02.11.2026) */
  puffer_tage?: number | null
  /** nur Fertigartikel: ohne Verpackungsmaterial (lose Ware im IBC), braucht keine Gebindegruppe (02.11.2026) */
  verpackungsfrei?: boolean
  /** Spalte in der Kladde (Ordnung der Blöcke); gleiche Zahl bei zwei Fertigartikeln = ein Block mit
   *  gemeinsamer Produktionsspalte. Gesetzt wird sie über „einordnen" (02.11.2026). */
  reihenfolge?: number | null
}

/** Wo ein Artikel in der Kladde steht - je Blatt ein Eintrag (`GET …/kladde-platz`, 02.11.2026). */
export interface KladdePlatz {
  blatt: string
  /** Titel des Blocks wie Zeile 1 der Excel */
  block: string
  /** Produktionsspalte des Blocks („10578 + 10535“ bei einer Sammelzelle) */
  spalte?: string | null
  position: number
  von: number
  davor?: string | null
  danach?: string | null
}

/** Antwort von `POST …/einordnen`: der neue Platz, und wie viele Artikel dafür nach hinten rückten. */
export interface EinordnenAntwort {
  artikelnummer: string
  reihenfolge_vorher?: number | null
  reihenfolge: number
  verschoben?: number
  platz?: KladdePlatz[]
}

/** Welches Material welcher Fertigartikel zieht - seit 02.11.2026 mit Gültigkeit (leer = offen). */
export interface Kopplung {
  id: number
  material: string
  material_bezeichnung: string
  fertigartikel: string
  fertigartikel_bezeichnung: string
  /** aus einer Regel oder dem Excel-Import entstanden - kommt mit der Regel wieder */
  hergeleitet?: boolean
  gueltig_ab?: string | null
  gueltig_bis?: string | null
  gilt_heute?: boolean
}

export interface KladdeSpalte {
  schluessel: string
  artikelnummer: string
  bezeichnung: string
  /**
   * produktion = Tagesproduktion eines Fertigartikels, bestand = Restbestand eines Materials,
   * summe = Spalte aus einer Summenregel
   */
  art: 'produktion' | 'bestand' | 'summe'
  /** Fertigartikel, zu dem die Spalte gehört - bildet die Produktblöcke der Excel-Kladde ab */
  gruppe: string
  hersteller: string | null
  einheit: string
  mindestbestand_t: number | null
}

export interface KladdeZeile {
  tag: string
  werte: Record<string, number | null>
}

export interface Kladde {
  gruppe: string
  spalten: KladdeSpalte[]
  zeilen: KladdeZeile[]
  /** Wareneingaenge und Inventuren im Zeitraum - eigene Zeilen in der Excel */
  ereignisse: Ereignis[]
  vermerke: Vermerk[]
}

export type Importstatus = 'offen' | 'gesperrt' | 'gebucht' | 'verworfen' | 'fehler'

export interface ImportLaufKurz {
  id: number
  quelle: 'mail' | 'upload' | 'freitext' | 'inventur' | 'selectline'
  dokumenttyp: string
  status: Importstatus
  dateiname: string | null
  modell: string | null
  kontrolle_bestanden: boolean | null
  angelegt_am: string
  geprueft_am: string | null
  entschieden_am?: string | null
  /** wer gebucht oder verworfen hat; „automatisch (SelectLine)", wenn die Plattform selbst gebucht hat */
  entschieden_von?: string | null
  anzahl_positionen: number
  anzahl_fehlende_artikel: number
  /** offene und beantwortete Rueckfragen - das, was noch zu erledigen ist */
  anzahl_rueckfragen: number
}

export interface ImportPosition {
  id: number
  artikelnummer: string
  bezeichnung: string | null
  tag: string | null
  /** woher der Tag stammt: aus der Zeile oder aus dem Dokumentkopf */
  tag_quelle: string | null
  charge: string | null
  menge_original: number | null
  einheit_original: string | null
  menge_t: number | null
  alt_wert_t: number | null
  neu_wert_t: number | null
  differenz_t: number | null
  rolle: string | null
  gehoert_zu: string | null
  bewegungsart: string | null
  uebernehmen: boolean
  hinweis: string | null
  wert_je_einheit?: number | null
  gesamtwert?: number | null
  /** wenn die Bewertung beim Gegenrechnen umgerechnet oder als unstimmig erkannt wurde */
  wert_hinweis?: string | null
}

/**
 * Ein Artikel, den ein Import nennt, den es aber noch nicht in den Stammdaten gibt.
 * Alles außer Nummer und Bezeichnung ist ein Vorschlag des Backends und wird hier bearbeitbar
 * angezeigt - die MVN entscheidet der Mensch.
 */
export interface FehlenderArtikel {
  artikelnummer: string
  bezeichnung: string
  art: Artikelart
  einheit: string
  anzahl_positionen: number
  rollen: string[]
  gebindegewicht_kg: number | null
  mvn: number | null
  mvn_begruendung: string | null
  gebindegruppe: string | null
  aktiv: boolean
  hinweis: string | null
}

/** Der Kurzname ist beim Anlegen freiwillig - wer Artikel aus einer Rückfrage anlegt, kennt ihn noch nicht. */
export type ArtikelEingabe = Omit<Artikel, 'id' | 'kurzname'> & { kurzname?: string | null }

export interface ArtikelSammelAntwort {
  angelegt: string[]
  uebersprungen: string[]
  abgelehnt: { artikelnummer: string; grund: string }[]
}

export interface Pruefbefund {
  schwere: Schwere
  regel: string
  text: string
}

/** Ein früherer Lauf mit genau derselben Datei (Prüfsumme) - kein Fehler, aber schon Gebuchtes wird bei der Freigabe übersprungen. */
export interface DoppelterLauf {
  lauf_id: number
  status: Importstatus
  angelegt_am: string
}

export interface ImportLauf extends ImportLaufKurz {
  /** bei unbekannter Berichtsart: wie das Modell das Dokument beschreibt */
  struktur?: string | null
  positionen: ImportPosition[]
  befunde: Pruefbefund[]
  /** frühere Läufe derselben Datei; optional, bis das Backend es liefert (17.09.2026) */
  doppelt?: DoppelterLauf[]
}

/** Antwort der Freigabe. `uebersprungen` und `hinweise` kommen erst mit dem Doppelt-Schutz des Backends (17.09.2026). */
export interface FreigabeAntwort {
  status: string
  buchungen: number
  bestellungen: BestellungAbgleich[]
  uebersprungen?: number
  hinweise?: string[]
  /** Rückfragen, die beim Buchen noch offen waren - jetzt „übergangen“, eine Antwort lässt sich nachtragen (02.11.2026) */
  rueckfragen_uebergangen?: { id: number; anlass?: string; frage: string; antwort?: string | null }[]
}

export type Regelart =
  | 'kopplung'
  | 'umrechnung'
  | 'gruppe'
  | 'mindestbestand'
  | 'summenspalte'
  | 'stammdaten'
  | 'artikel'
  | 'einstellung'

/** Was das Löschen einer Regel in den Stammdaten zurückgesetzt hat - und was es stehen ließ. */
export interface RegelRueckbau {
  zurueckgesetzt: string[]
  uebersprungen: string[]
}

export interface Regel {
  id: number
  art: Regelart
  titel: string
  parameter: Record<string, unknown>
  freitext: string | null
  begruendung: string | null
  modell: string | null
  aktiv: boolean
  angelegt_am: string
}

export interface RegelVorschauZeile {
  gegenstand: string
  feld: string
  alt: string
  neu: string
}

export interface RegelVorschlag {
  art: Regelart
  titel: string
  parameter: Record<string, unknown>
  begruendung: string | null
  sicherheit: Sicherheit
  /** leer heißt: die Regel lässt sich übernehmen */
  einwaende: string[]
  vorschau: RegelVorschauZeile[]
}

export interface RegelVorschlagAntwort {
  vorschlaege: RegelVorschlag[]
  rueckfragen: string[]
}

export interface ErfassungVorlage {
  schluessel: string
  titel: string
  hinweis: string
}

export interface ErfassungVorschlagPosition {
  artikelnummer: string | null
  bezeichnung: string | null
  tag: string | null
  /** woher der Tag stammt: aus der Zeile oder aus dem Dokumentkopf */
  tag_quelle: string | null
  charge: string | null
  menge: number | null
  einheit: string | null
  bewegungsart: string | null
  sicherheit: Sicherheit
  begruendung: string | null
}

export interface ErfassungBilanz {
  erkannt: number
  zugeordnet: number
  unbekannt: number
  unsicher: number
  widersprueche: number
}

export interface ErfassungAntwort {
  import_lauf_id: number | null
  positionen: ErfassungVorschlagPosition[]
  rueckfragen: string[]
  bilanz: ErfassungBilanz
  /** Materialien der betroffenen Gruppen, die im Text nicht vorkamen (nur Zählzettel). */
  nicht_erfasst: string[]
}

// --- Disposition --------------------------------------------------------------------------------

export type Bestellstatus = 'geplant' | 'in_produktion' | 'beim_hersteller' | 'beauftragt' | 'geliefert'

export interface Produktionsplan {
  id: number
  tag: string
  fertigartikel: string
  bezeichnung: string
  menge_t: number
  bemerkung: string | null
}

export interface ProduktionsplanEingabe {
  tag: string
  fertigartikel: string
  menge_t: number
  bemerkung?: string | null
}

export interface Bestellung {
  id: number
  material: string
  bezeichnung: string
  menge_t: number
  menge_original: number | null
  einheit_original: string | null
  status: Bestellstatus
  liefer_tag: string | null
  liefer_kw: string | null
  ueberfaellig: boolean
  /** Liefertermin liegt hinter dem Ende der Reichweite - Termin vorziehen oder Zwischenabruf. */
  nach_reichweite: boolean
  beleg: string | null
  bemerkung: string | null
  angelegt_am: string
  erledigt_am: string | null
}

export interface BestellungEingabe {
  material: string
  menge_t?: number | null
  menge_original?: number | null
  einheit_original?: string | null
  status: Bestellstatus
  liefer_tag?: string | null
  beleg?: string | null
  bemerkung?: string | null
}

export interface Sperrung {
  id: number
  material: string
  bezeichnung: string
  menge_t: number
  grund: string | null
  seit: string
  aufgehoben_am: string | null
  /** Die gesperrte Charge steht im jüngsten SelectLine-Bestand nicht mehr - aufheben? Nichts wird automatisch
   * aufgehoben (Runde 4, 02.11.2026, Mail K). */
  selectline_hinweis?: string | null
}

export interface SperrungEingabe {
  material: string
  menge_t: number
  grund?: string | null
  seit?: string | null
}

/** Eine Materialspalte des Dispositionsblatts - alle Mengen in Tonnen Fertigprodukt. */
export interface DispositionZeile {
  artikelnummer: string
  bezeichnung: string
  hersteller: string | null
  block: string
  bestand_t: number
  bestand_am: string | null
  gesperrt_t: number
  geplant_t: number
  lieferungen_t: number
  zwischensumme_t: number
  mindestbestand_t: number | null
  knapp: boolean
  /** Ø der nächsten drei Monate ab Stichtag */
  verbrauch_je_monat_t: number | null
  /** Ø der nächsten zwölf Monate ab Stichtag */
  verbrauch_12_monate_t?: number | null
  verbrauch_quelle: string
  reicht_bis: string | null
  reicht_kw: string | null
  beim_hersteller_t: number
  beauftragt_t: number
  geplant_bestellt_t: number
  gesamtbestand_t: number
  gesamt_reicht_bis: string | null
  gesamt_reicht_kw: string | null
  lieferzeit_tage: number | null
  bestellen_bis: string | null
  bestellen_kw: string | null
  handlung: string | null
  bestellungen: Bestellung[]
  sperrungen: Sperrung[]
  /** das Reichweitenband: beide Enden, das fruehere ist massgeblich */
  reicht_bis_ist: string | null
  reicht_bis_ist_kw: string | null
  reicht_bis_forecast: string | null
  reicht_bis_forecast_kw: string | null
  gesamt_reicht_bis_ist: string | null
  gesamt_reicht_bis_forecast: string | null
  /** Verwendbarkeit: die Frist gewinnt, wenn sie frueher endet */
  frist_bis: string | null
  frist_grund: string | null
  /** Die Frist selbst; frist_bis ist um den Vorlauf früher. */
  frist_ende: string | null
  frist_vorlauf_tage: number | null
  /** false = „Frist ungeklärt" (gerechnet wird trotzdem mit ihr); leer = keine Frist (02.11.2026) */
  frist_bestaetigt?: boolean | null
  /** Das laufende Jahr liegt weit über dem Vorjahr - die Ist-Kurve rechnet mit dem Satz des laufenden Jahres. */
  trend_hinweis?: string | null
  /** Bis zu diesem Tag ist Produktion gebucht (über alle Fertigartikel). */
  produktion_gebucht_bis?: string | null
  /** Satz, wenn die gebuchte Produktion älter als 7 Tage ist. */
  datenstand_hinweis?: string | null
  abweichung_ist_forecast_tage: number | null
  verwendbar_bis: string | null
  verwendbar_kw: string | null
  in_produktion_t: number
  /** Bestellregeln des Lieferanten und der zweite Termin (Abruf) */
  lieferant: string | null
  lieferant_id: number | null
  layout_vorlauf_tage: number | null
  /** Verbundmaterial: Vorlauf der Vorstufen (Alu, Pergament) vor der Produktionszeit */
  vorstufen_tage: number | null
  vorstufen: string | null
  vorstufen_bestellen_bis: string | null
  vorstufen_bestellen_kw: string | null
  puffer_tage: number
  abruf_vorlauf_tage: number
  abruf_puffer_tage: number
  abruf_bis: string | null
  abruf_kw: string | null
  stueck_je_palette: number | null
  paletten_bestand: number | null
  paar_gruppe: string | null
  vermerke: Vermerk[]
}

export interface Lieferantengruppe {
  name: string
  lieferant_id: number | null
  materialien: string[]
  mindestabruf_paletten: number | null
  abruf_faellig: string[]
  nachauflage_faellig: string[]
  /** „Abruf genau 33 Paletten · Nachauflage ein Vielfaches von 33 Paletten“, „mindestens 30 Paletten“; leer = keine Grenze */
  mengenregel?: string | null
}

export interface Disposition {
  gruppe: string
  stichtag: string
  horizont_bis: string
  puffer_tage: number
  zeilen: DispositionZeile[]
  plan: Produktionsplan[]
  lieferanten: Lieferantengruppe[]
  rechenbasis?: Rechenbasis
  /** Bis zu diesem Tag ist Produktion gebucht (02.11.2026). */
  produktion_gebucht_bis?: string | null
  /** Satz, wenn die gebuchte Produktion älter als 7 Tage ist - Bestand und Reichweiten stehen auf diesem Stand. */
  datenstand_hinweis?: string | null
  /** Nach welchem Takt Stichtag und Horizont gelten, wenn die Ansicht keinen eigenen Stichtag setzt (02.11.2026). */
  takt?: Takt
}

/** Womit die maßgebliche Reichweite gerechnet wird, wenn Ist und Forecast auseinanderliegen. */
export type Rechenbasis = 'frueher' | 'ist' | 'forecast' | 'spaeter' | 'mischung'

/** Die Rechenbasis, die gilt, wenn niemand eine nennt (`GET/PUT /api/disposition/rechenbasis`, 02.11.2026). */
export interface RechenbasisEinstellung {
  rechenbasis: Rechenbasis
  beschreibung: string
  /** gilt, wenn nichts eingestellt ist */
  standard: Rechenbasis
  /** Wert → Beschreibung des Backends */
  moeglich: Record<string, string>
}

/** Anfrage „Was wäre, wenn" (`POST /api/disposition/reichweite`) - ändert nichts. */
export interface ReichweiteAnfrage {
  material: string
  /** angenommener Bestand am Tag `ab` in t FP; leer = gebuchter Bestand abzüglich gesperrter Ware */
  bestand_t?: number | null
  ab?: string | null
  zugaenge?: { tag: string; t: number }[]
  rechenbasis?: Rechenbasis | null
  bestand_am?: string | null
  monate?: number
}

export interface ReichweiteMonat {
  monat: string
  bedarf_t: number
  zugang_t: number
  bestand_ende_t: number
}

export interface ReichweiteErgebnis {
  material: string
  bezeichnung: string
  ab: string
  bestand_t: number
  /** angenommen oder gebucht (mit Datum, abzüglich gesperrt) */
  bestand_quelle: string
  rechenbasis: Rechenbasis
  reicht_bis: string | null
  reicht_kw: string | null
  massgeblich_quelle?: string
  reicht_bis_ist?: string | null
  reicht_bis_ist_kw?: string | null
  reicht_bis_forecast?: string | null
  reicht_bis_forecast_kw?: string | null
  /** wann auch der letzte Zugang aufgebraucht ist - nur verschieden von reicht_bis, wenn vorher ein Engpass liegt */
  ende_nach_zugaengen?: string | null
  engpaesse?: { leer_ab: string; zugang_am: string }[]
  bestand_am?: string | null
  bestand_am_t?: number | null
  verbrauch_je_monat_t?: number | null
  verbrauch_12_monate_t?: number | null
  verlauf?: ReichweiteMonat[]
  trend_hinweis?: string | null
  datenstand_hinweis?: string | null
}

export const RECHENBASEN: { wert: Rechenbasis; titel: string }[] = [
  { wert: 'frueher', titel: 'Sicherheit: früheres Ende von Ist und Forecast' },
  { wert: 'ist', titel: 'nur Ist-Verbrauch' },
  { wert: 'forecast', titel: 'nur Forecast GF' },
  { wert: 'mischung', titel: 'Ist bis Quartalsende, dann Forecast' },
  { wert: 'spaeter', titel: 'späteres Ende von Ist und Forecast' },
]

/** Nach welchem Rhythmus Stichtag und Horizont ohne eigene Angabe gelten (`GET/PUT /api/disposition/takt`, 02.11.2026). */
export type Takt = 'dienstag_freitag' | 'heute'

/** Der Takt, der gilt, wenn niemand einen nennt: Dispositionsblatt, Export, Heute, Fällig-Liste und Assistent. */
export interface TaktEinstellung {
  takt: Takt
  beschreibung: string
  /** gilt, wenn nichts eingestellt ist */
  standard: Takt
  /** Wert → Beschreibung des Backends */
  moeglich: Record<string, string>
  /** Stichtag, den ein Blatt ohne Angabe heute zeigt */
  stichtag: string
  /** bis dahin zählen geplante Produktion und Lieferungen in die Zwischensumme */
  horizont_bis: string
}

export const TAKTE: { wert: Takt; titel: string }[] = [
  { wert: 'dienstag_freitag', titel: 'Wie die Kladde: Bestand vom letzten Dienstag, bis Freitag der Folgewoche' },
  { wert: 'heute', titel: 'Bestand von heute, Horizont in Tagen' },
]

/** Wann zuletzt ein SelectLine-Report gebucht wurde und bis wann seine Zahlen reichen.
 *  Für „ist die Kladde aktuell?" zählt der Datenstand: ein nachträglich hochgeladener Bericht ist
 *  heute gebucht und trotzdem Wochen alt. */
export interface ReportStand {
  dokumenttyp: string
  lauf_id: number | null
  gebucht_am: string | null
  tage_her: number | null
  datenstand?: string | null
  datenstand_tage_her?: number | null
}

export interface PlanmengenExcelAntwort {
  gesetzt: number
  fertigartikel: string[]
  jahre: number[]
  unbekannt: string[]
}

// --- Inventur -----------------------------------------------------------------------------------

export type Inventurstatus = 'offen' | 'zur_freigabe' | 'gesperrt' | 'abgeschlossen'

export interface Zaehleinheit {
  bezeichnung: string
  /** Stück je Einheit; leer = Stück direkt */
  faktor: number | null
  sperrlager: boolean
}

export interface Zaehlposition {
  bezeichnung: string
  anzahl: number
  faktor?: number
  menge?: number
  sperrlager?: boolean
}

export interface Inventur {
  id: number
  stichtag: string
  beleg: string | null
  bemerkung: string | null
  status: Inventurstatus
  import_lauf_id: number | null
  anzahl_materialien: number
  anzahl_gezaehlt: number
  angelegt_am: string
}

/** Eine Zeile des Abgleichs: Soll aus SelectLine gegen die Zählung, samt Zähleinheiten fürs Formular. */
export interface InventurZeile {
  artikelnummer: string
  bezeichnung: string
  gruppe: string | null
  einheit: string
  mvn: number | null
  zaehleinheiten: Zaehleinheit[]
  positionen: Zaehlposition[]
  gezaehlt: number | null
  gesperrt: number
  frei: number | null
  gezaehlt_t: number | null
  soll: number | null
  soll_t: number | null
  soll_quelle: string | null
  differenz: number | null
  differenz_t: number | null
  wert_je_einheit: number | null
  differenz_wert: number | null
  vermerk: string | null
  hinweis: string | null
  /** Ist laut SelectLine-Inventurbeleg - alle Chargen und Läger (auch Sperrlager) */
  ist_selectline?: number | null
  ist_selectline_t?: number | null
  /** „Handzählung" oder „SelectLine (Lauf n)" - woraus die Differenz gerechnet ist */
  ist_quelle?: string | null
}

export interface InventurBlatt {
  inventur: Inventur
  gruppen: string[]
  zeilen: InventurZeile[]
}

export interface ZaehlzettelZaehlung {
  artikelnummer: string
  bezeichnung: string
  positionen: Zaehlposition[]
  gezaehlt: number
  gesperrt: number
  sicherheit: Sicherheit
  begruendung: string | null
}

export interface ZaehlzettelAntwort {
  zaehlungen: ZaehlzettelZaehlung[]
  rueckfragen: string[]
}

export interface Jahresmonat {
  stichtag: string
  soll: number | null
  soll_t: number | null
  ist: number | null
  ist_t: number | null
  differenz: number | null
  differenz_t: number | null
  differenz_wert: number | null
  status: Inventurstatus
}

export interface Jahreszeile {
  artikelnummer: string
  bezeichnung: string
  gruppe: string | null
  monate: Record<string, Jahresmonat>
  summe_differenz_t: number
  summe_differenz_wert: number
}

/**
 * Eine Frage des Systems an Frau Barth. Sie antwortet in eigenen Worten, ein Agent macht daraus
 * einen Vorschlag (aktionen), und erst die Freigabe wendet ihn an.
 */
export type Rueckfragestatus = 'offen' | 'beantwortet' | 'erledigt' | 'verworfen' | 'uebergangen'

export interface Rueckfrage {
  id: number
  import_lauf_id: number | null
  inventur_id: number | null
  /** Pruefregel, aus der die Frage kam */
  anlass: string
  schwere: Schwere
  frage: string
  kontext: Record<string, unknown>
  status: Rueckfragestatus
  antwort: string | null
  aktionen: Record<string, unknown>[]
  erklaerung: string | null
  sicherheit: Sicherheit | null
  rueckfrage_des_agenten: string | null
  wirkung: Record<string, unknown>[] | Record<string, unknown> | null
  angelegt_am: string
  beantwortet_am: string | null
  erledigt_am: string | null
}

export interface RueckfrageWirkung {
  rueckfrage: Rueckfrage
  lauf_status: string | null
  befunde: Pruefbefund[]
  wirkung: Record<string, unknown>[]
}

// --- Lieferanten, Fristen, Planmengen, Vermerke ------------------------------------------------

/**
 * Eine Preisstufe. Sie hat genau eine Schwelle: ab Stück oder (seit 02.11.2026) ab Paletten - gezählt über die
 * Positionen ihrer Artikelgruppe. Der Preis bleibt je 1.000 Stück.
 */
export interface Staffel {
  id?: number
  ab_stueck: number | null
  ab_paletten?: number | null
  preis_je_1000: number
  artikelgruppe: string | null
  bemerkung: string | null
}

/**
 * Wie die Paletten eines Abrufs bzw. einer Nachauflage über alle Artikel zusammen liegen müssen (Runde 4,
 * 02.11.2026, Berry: „Abruf insgesamt exakt 33 Paletten, Nachproduktionen 33 oder 66“): mindestens N, genau N
 * oder ein Vielfaches von N. Leer beim Lieferanten = der Mindestabruf als Untergrenze, bei einer Gruppe = wie der
 * Lieferant. Paletten leer = der Mindestabruf.
 */
export type Mengenregel = 'mindestens' | 'genau' | 'vielfache'

export interface MengenregelWerte {
  abruf_mengenregel?: Mengenregel | null
  abruf_paletten?: number | null
  nachauflage_mengenregel?: Mengenregel | null
  nachauflage_paletten?: number | null
}

/** Die wirksame Regel in Worten, vom Backend gerechnet (mit Geerbtem und Mindestabruf); leer = keine Grenze. */
export interface MengenregelTexte {
  abruf_mengenregel_text?: string | null
  nachauflage_mengenregel_text?: string | null
}

export interface Lieferant extends MengenregelWerte, MengenregelTexte {
  id: number
  name: string
  kurzname: string | null
  mindestabruf_paletten: number | null
  abruf_vorlauf_tage: number
  abruf_puffer_tage: number
  puffer_tage: number
  produktionszeit_tage: number | null
  abruffrist_monate: number | null
  preis_gilt: 'bestellung' | 'lieferung'
  bemerkung: string | null
  aktiv: boolean
  staffeln: Staffel[]
  artikel: string[]
  /** Artikelgruppen des Lieferanten (02.11.2026); fehlt bei einem älteren Backend */
  gruppen?: Artikelgruppe[]
  /** die Artikel mit Bezeichnung, Blatt, Gruppe und wirksamen Werten (02.11.2026); fehlt bei einem älteren Backend */
  artikel_details?: LieferantArtikel[]
  /** Hinweis zur letzten Änderung, z.B. nach dem Löschen einer Gruppe, deren Staffeln stehen bleiben */
  hinweis?: string | null
}

/** `id` gesetzt: diesen Lieferanten aendern (auch umbenennen); leer: nach Name anlegen oder aendern. */
export type LieferantEingabe = Omit<
  Lieferant,
  'id' | 'staffeln' | 'artikel' | 'gruppen' | 'artikel_details' | 'hinweis' | keyof MengenregelTexte
> & {
  id?: number | null
}

/**
 * Die Werte, die eine Artikelgruppe vom Lieferanten übersteuert. Ebenen: Standard → Lieferant →
 * Artikelgruppe → Artikel; leer (null) heißt geerbt.
 */
export type RegelFeld =
  | 'mindestabruf_paletten'
  | 'abruf_vorlauf_tage'
  | 'abruf_puffer_tage'
  | 'puffer_tage'
  | 'produktionszeit_tage'
  | 'abruffrist_monate'

/** Eine Artikelgruppe eines Lieferanten („Wickler“) - Werte roh, null = wie der Lieferant. */
export interface Artikelgruppe extends MengenregelWerte, MengenregelTexte {
  id: number
  name: string
  bemerkung: string | null
  mindestabruf_paletten: number | null
  abruf_vorlauf_tage: number | null
  abruf_puffer_tage: number | null
  puffer_tage: number | null
  produktionszeit_tage: number | null
  abruffrist_monate: number | null
  preis_gilt: 'bestellung' | 'lieferung' | null
  /** Artikelnummern der Mitglieder */
  artikel: string[]
}

/** `id` gesetzt: diese Gruppe ändern (auch umbenennen); leer: nach Name ändern oder anlegen. Null = geerbt. */
export type ArtikelgruppeEingabe = Omit<Artikelgruppe, 'id' | 'artikel' | keyof MengenregelTexte> & { id?: number | null }

/** Woher ein wirksamer Wert kommt. */
export type WirkQuelle = 'artikel' | 'gruppe' | 'lieferant' | 'standard'

export interface WirksamWert {
  wert: number | string | null
  quelle: WirkQuelle
}

/** Ein Artikel in der Übersicht des Lieferanten. */
export interface LieferantArtikel {
  artikelnummer: string
  bezeichnung: string
  kurzname: string | null
  art: Artikelart
  /** das Blatt der Kladde */
  gebindegruppe: string | null
  aktiv: boolean
  artikelgruppe: string | null
  artikelgruppe_id: number | null
  /** je Regelfeld der Wert, der für diesen Artikel gilt, und seine Ebene */
  wirksam: Partial<Record<RegelFeld, WirksamWert>>
}

export interface Frist {
  id: number
  material: string
  bezeichnung: string
  verwendbar_bis: string
  verwendbar_kw: string | null
  grund: string
  menge_t: number | null
  vorlauf_tage: number | null
  /** verwendbar_bis minus Vorlauf - bis dahin muss das Material verarbeitet sein. */
  produktion_bis: string | null
  bemerkung: string | null
  /** false = „Frist ungeklärt" - gerechnet wird trotzdem mit ihr (02.11.2026); fehlt = geklärt */
  bestaetigt?: boolean
  aufgehoben_am: string | null
}

export interface PlanIstMonat {
  monat: number
  vorjahr: number | null
  forecast: number | null
  ist: number | null
  differenz: number | null
}

export interface PlanIstQuartal {
  quartal: number
  vorjahr: number | null
  forecast: number | null
  ist: number | null
  differenz: number | null
}

/** Eine Spaltengruppe des Blatts „PLAN - IST": ein Fertigartikel, zwölf Monate, vier Quartale. */
export interface PlanIst {
  artikelnummer: string
  bezeichnung: string
  gruppe: string | null
  jahr: number
  monate: PlanIstMonat[]
  quartale: PlanIstQuartal[]
  summe_vorjahr: number | null
  summe_forecast: number | null
  summe_ist: number | null
  summe_differenz: number | null
  mittel_vorjahr: number | null
  mittel_forecast: number | null
  mittel_ist: number | null
}

export interface Planmenge {
  id?: number
  fertigartikel: string
  bezeichnung?: string
  jahr: number
  monat: number
  /** null beim Schreiben: den Monat dieser Quelle loeschen */
  menge_t: number | null
  quelle: string
  bemerkung: string | null
}

export type Vermerkart = 'layout' | 'kontrakt' | 'test' | 'frist' | 'lieferung' | 'sonstiges'

export interface Vermerk {
  id: number
  artikelnummer: string | null
  art: Vermerkart
  text: string
  tag: string | null
  bis: string | null
  /** Mit Fälligkeit ist der Vermerk eine Aufgabe, die bis zur Erledigung in der Übersicht steht. */
  faellig: string | null
  erledigt_am: string | null
  angelegt_am: string
}

export interface Ereignis {
  tag: string
  art: 'zugang' | 'inventur' | 'korrektur'
  artikelnummer: string
  menge_t: number
  beleg: string | null
}

// --- Bestellvorschlag ------------------------------------------------------------------------------

export interface VorschlagPosition {
  artikelnummer: string
  bezeichnung: string
  paletten: number
  stueck: number
  menge_t: number
  reicht_vorher: string | null
  reicht_vorher_kw: string | null
  reicht_nachher: string | null
  reicht_nachher_kw: string | null
  preis_je_1000: number | null
  kosten: number | null
  hinweise: string[]
  /** Artikelgruppe des Lieferanten, zu der die Position zählt; fehlt bei einem älteren Backend */
  artikelgruppe?: string | null
}

/**
 * Eine Artikelgruppe in einer Variante - Summe, Mindestabruf, Staffel. Liefert das Backend das nicht, rechnet die
 * Bestellen-Seite es zur Anzeige aus Positionen und Lieferant selbst.
 */
export interface VarianteGruppe {
  artikelgruppe: string | null
  paletten: number
  stueck: number | null
  mindestabruf_paletten: number | null
  mindestabruf_erfuellt?: boolean | null
  /** die Mengenregel der Gruppe (mindestens | genau | vielfache) und in Worten („genau 33 Paletten“) */
  mengenregel?: Mengenregel | null
  mengenregel_text?: string | null
  /** erreichte Staffel in Worten („ab 20 Pal.“) */
  staffel?: string | null
  preis_je_1000: number | null
  naechste_staffel: string | null
  kosten: number | null
}

export interface Variante {
  titel: string
  zielreichweite_tage: number
  paletten_gesamt: number
  stueck_gesamt: number
  kosten_gesamt: number | null
  staffel_ab_stueck: number | null
  /** erreichte Staffel, wenn sie in Paletten zählt (02.11.2026) */
  staffel_ab_paletten?: number | null
  preis_je_1000: number | null
  naechste_staffel: string | null
  einwaende: string[]
  /** je Artikelgruppe Summe, Mindestabruf und Staffel - optional */
  gruppen?: VarianteGruppe[]
  reicht_frueheste: string | null
  reicht_spaeteste: string | null
  positionen: VorschlagPosition[]
}

export type Vorschlagstatus = 'entwurf' | 'entschieden' | 'verworfen'

export interface Bestellvorschlag {
  id: number
  lieferant: string
  lieferant_id: number
  art: 'abruf' | 'nachauflage'
  stichtag: string
  zielreichweite_tage: number
  status: Vorschlagstatus
  varianten: Variante[]
  gewaehlt: number | null
  bemerkung: string | null
  /** KI-Empfehlung für den Chef, auf Knopfdruck eingeholt und am Vorschlag gespeichert. */
  empfehlung: Empfehlung | null
  angelegt_am: string
  entschieden_am: string | null
}

export interface Empfehlung {
  variante: number
  begruendung: string
  risiken: string[]
  fragen: string[]
  mail_betreff: string
  mail_text: string
  modell?: string
  erzeugt_am?: string
}

export type ExcelArt =
  | 'kladde'
  | 'disposition'
  | 'zaehleinheiten'
  | 'inventur_auswertung'
  | 'planmengen'
  | 'bestandsliste'
  | 'monatsproduktion'
  | 'ignorieren'

export interface ExcelBlatt {
  blatt: string
  art: ExcelArt
  gruppe: string
  aktiv: boolean
  zeilen: number
  spalten: number
  vorschau: string[][]
  warnungen: string[]
  hinweise: string[]
  sicherheit?: Sicherheit
  artikel_neu?: string[]
  artikel_bekannt?: string[]
  kopplungen?: Record<string, string[]>
  /** Blöcke, deren Kopplung nur ein Vorschlag ist und noch nicht bestätigt wurde (PUT mit denselben Nummern bestätigt). */
  offene_vorschlaege?: string[]
  /** Die Gruppe des Blatts gibt es noch nicht - der Server nennt sie, damit die Seite nichts aus Hinweisen lesen muss. */
  gruppe_neu?: string | null
  /** Die Gruppen, die es gibt - die Auswahl der Frage „Neue Gruppe" nimmt nur daraus. */
  gruppen_vorhanden?: string[]
  /** Datum des Blattinhalts (Kladde: letzter Tag, Kladde Material: Stichtag, PLAN-IST: Jahresende) - ISO. */
  datenstand?: string | null
  /** Ein früherer Lauf hat für Art und Gruppe schon einen jüngeren Stand gebucht - das Blatt ist deshalb abgewählt. */
  aelterer_stand?: { lauf_id: number; datenstand: string } | null
  /** Was der Server für das Häkchen vorschlägt (false bei älterem Stand); `aktiv` ist der Stand nach der Nutzerin. */
  vorschlag_aktiv?: boolean
  /** Warum der Server das Blatt abgewählt hat (stillgelegt, älterer Stand, keine Tageszeilen) - als Feld, nicht aus Hinweisen gelesen. */
  abwahl_grund?: string | null
  /** Aus dem Entscheidungsgedächtnis übernommen - die Seite fragt diese Punkte nicht erneut (18.09.2026). */
  entscheidungen?: ExcelEntscheidung[]
  kladde?: {
    fertigartikel: ArtikelKurz[]
    materialien: (ArtikelKurz & { hersteller: string; block: string; fertigartikel: string[] })[]
    kopplungen_vorschlag: Record<string, string[]>
    zeitraum: { von: string | null; bis: string | null }
    zaehlung: { tageszeilen: number; inventuren: number; wareneingaenge: number }
  }
  disposition?: { stichtag: string | null; materialien: { nummer: string; block: string }[] }
  zaehleinheiten?: { artikel: (ArtikelKurz & { einheiten: unknown[] })[]; gezaehlt?: number }
  einheiten_uebernehmen?: boolean
  auswertung?: { artikel: (ArtikelKurz & { mvn: number | null })[] }
  planmengen?: { werte: number; fertigartikel: string[]; jahre: number[] }
}

/** Ein Blatt, das beim Ausführen nicht geladen wurde - mit dem Grund. */
/** Was die Rücknahme einer gebuchten Freigabe zurückgedreht hat. */
export interface Ruecknahme {
  status: string
  bewegungen: number
  produktion: number
  plan: number
  bestellungen: string[]
  offen_geblieben: string[]
  /** Läufe, die dieser Lauf ersetzt hatte (korrigierter Tagesbericht) - jetzt wieder gebucht (02.11.2026) */
  wieder_gebucht?: number[]
}

export interface ExcelUebersprungen {
  blatt: string
  art: ExcelArt
  gruppe: string | null
  grund: string
}

export interface ExcelProtokollBlatt {
  blatt: string
  art: ExcelArt
  gruppe?: string
  warnungen: string[]
  abgleich?: { artikel: string; was: string; excel: number | string | null; excel_gesamt?: string | null; system: number | null }[]
  [zahl: string]: unknown
}

/** Was die KI am Excel-Plan getan hat - steht am Plan und am Lauf. */
export interface KiProtokoll {
  genutzt: boolean
  modell?: string
  geaendert?: string[]
  hinweise?: string[]
  fehler?: string
}

/** Ein früherer Lauf derselben Datei - ein zweiter Import ersetzt, was der erste gebucht hat. */
export interface ExcelDoppelt {
  lauf_id: number
  status: string
  angelegt_am: string
  entschieden_am: string | null
}

export interface ExcelImport {
  id: number
  datei: string | null
  status: string
  /** Solange false, liest der Server den Plan noch (mit KI, bis zu einigen Minuten) - erneut laden. */
  bereit: boolean
  fehler: string | null
  plan: { datei: string; blaetter: ExcelBlatt[]; ki?: KiProtokoll; doppelt?: ExcelDoppelt[] }
  ki: KiProtokoll | null
  protokoll: { datei: string; blaetter: ExcelProtokollBlatt[]; uebersprungen?: ExcelUebersprungen[] } | null
  arten: Record<ExcelArt, string>
}

// --- Entscheidungen, Datenprüfung, Journal (18.09.2026) -----------------------------------------

/** Die Arten, die das Gedächtnis kennt - je Blatt (und je Block bei Kopplungen). */
export type EntscheidungArt = 'blattart' | 'gruppe' | 'aktiv' | 'kopplung'

/** Eine gemerkte Entscheidung, wie sie am Blatt eines Excel-Plans steht. */
export interface ExcelEntscheidung {
  art: EntscheidungArt
  /** bei Kopplungen der Blockname, sonst die erkannte Blattart - `null`, wenn der Server ihn nicht kennt */
  block: string | null
  wert: unknown
  /** wann die Entscheidung gemerkt wurde - der Server kennt sie in Altbeständen nicht immer */
  seit: string | null
  lauf_id: number | null
}

/** Eine gemerkte Entscheidung in der Liste der Einstellungen (`GET /api/entscheidungen`). */
export interface Entscheidung {
  id: number
  art: EntscheidungArt
  blatt: string
  block: string | null
  wert: unknown
  quelle: string
  lauf_id: number | null
  angelegt_am: string
  benutzt: number
  zuletzt_benutzt_am: string | null
}

/** Ein Befund der Datenprüfung: Titel, Text, betroffene Artikel und der Weg zur Stelle. */
export interface Befund {
  schluessel: string
  schwere: Schwere
  titel: string
  text: string
  artikel: string[]
  anzahl: number
  link: string
  /** Die Quittungen, die Artikel (oder den ganzen Befund) ausgeblendet haben - fehlt bei einem Backend vor dem 02.11.2026. */
  quittiert?: Quittung[]
}

/**
 * „Erledigt" an einem Befund der Datenprüfung: wer, wann, warum - und auf Wunsch, ab wann er wieder
 * vorgelegt wird. Ohne Artikelnummer gilt sie für den ganzen Befund (02.11.2026).
 */
export interface Quittung {
  id: number
  schluessel: string
  artikelnummer: string | null
  grund: string
  quittiert_von: string | null
  quittiert_am: string
  wiedervorlage: string | null
  aufgehoben_am: string | null
}

export interface QuittungEingabe {
  schluessel: string
  /** leer = der ganze Befund */
  artikel: string[]
  grund: string
  wiedervorlage: string | null
}

export interface DatenpruefungZaehler {
  fehler: number
  warnung: number
  hinweis: number
}

export interface Datenpruefung {
  geprueft_am: string
  zaehler: DatenpruefungZaehler
  befunde: Befund[]
  /** Befunde, deren Artikel alle quittiert sind - sie zählen nicht mehr mit. */
  erledigt?: Befund[]
}

/** Ein Eintrag des Journals: jede schreibende Aktion, mit dem Stand davor und dem Weg zurück. */
export interface JournalEintrag {
  id: number
  zeitpunkt: string
  quelle: string
  unterhaltung_id: number | null
  werkzeug: string | null
  titel: string
  methode: string
  pfad: string
  argumente: unknown
  vorher: unknown
  ergebnis_status: number | null
  /** der Grund, warum die Aktion eine Freigabe brauchte - leer, wenn keine nötig war */
  freigabe: string | null
  rueckgaengig_moeglich: boolean
  rueckgaengig_am: string | null
}

/** Der Zweitblick vor der Freigabe: regelbasierte Plausibilität, sperrt nichts. */
export interface Zweitblick {
  schwere: 'warnung' | 'hinweis'
  text: string
  artikelnummer: string | null
  position_id: number | null
}

export interface BeobachtungPunkt {
  nr: number
  schwere: 'warnung' | 'hinweis' | 'tipp'
  titel: string
  text: string
  artikel: string[]
  gruppen: string[]
  link: string
  frage: string | null
  rueckmeldung: 'stimmt' | 'stimmt_nicht' | null
  grund: string | null
}

export interface Beobachtung {
  id: number
  stichtag: string
  erzeugt_am: string
  modell: string | null
  punkte: BeobachtungPunkt[]
}

// --- Assistent ----------------------------------------------------------------------------------

export interface Unterhaltung {
  id: number
  titel: string | null
  /** Zu welchem Arbeitsbereich das Gespräch gehört - jeder Bereich hat seinen eigenen Assistenten. */
  bereich?: BereichId
  angelegt_am: string
  geaendert_am: string
  /** Eine Aktion wartet auf Freigabe - bis dahin nimmt das Gespräch keine neue Nachricht an. */
  wartet: boolean
  /** Ein Lauf arbeitet auf dem Server, auch wenn der Browser die Verbindung verloren hat (02.11.2026). */
  laeuft?: boolean
}

export interface WerkzeugAnzeige {
  id: string
  name: string
  titel: string
  argumente: Record<string, unknown>
  /** abgebrochen: nie gelaufen - das Gespräch ging danach weiter oder brach ab (02.11.2026) */
  zustand: 'laeuft' | 'fertig' | 'fehler' | 'wartet' | 'abgelehnt' | 'abgebrochen'
  text: string
  /** warum die Aktion eine Freigabe braucht („große Menge (über 100 t)") - nur bei `wartet` */
  grund?: string
  /** was die Aktion bewirkt, in Worten ohne Ids („Lieferant Beucke: Puffer 42 → 56 Tage") */
  beschreibung?: string | null
  /** für wen sie gilt („gilt für alle 12 Artikel von Beucke: …") - leer, wenn nur für das Genannte */
  umfang?: string | null
}

export interface NachrichtAnzeige {
  id: number
  rolle: 'user' | 'assistant'
  text: string
  werkzeuge: WerkzeugAnzeige[]
  angelegt_am: string
}

export interface UnterhaltungVoll {
  unterhaltung: Unterhaltung
  nachrichten: NachrichtAnzeige[]
}

/** Was der Strom des Assistenten schickt, ein Ereignis je Zeile. */
export type ChatEreignis =
  | { typ: 'text'; text: string }
  | { typ: 'aufruf'; id: string; name: string; titel: string; argumente: Record<string, unknown> }
  | { typ: 'ergebnis'; id: string; status: number; text: string; abgelehnt?: boolean }
  | {
      typ: 'freigabe'
      id: string
      name: string
      titel: string
      argumente: Record<string, unknown>
      grund?: string
      beschreibung?: string | null
      umfang?: string | null
    }
  | { typ: 'wartet' }
  | { typ: 'fertig' }
  | { typ: 'fehler'; text: string }
  | { typ: 'verbrauch'; prompt_tokens?: number; completion_tokens?: number; total_tokens?: number }

// --- Übersicht ----------------------------------------------------------------------------------

export interface OffenerHinweis {
  gruppe: string
  artikelnummer: string
  bezeichnung: string
  hinweis: string
  lieferant: string | null
  reicht_kw: string | null
}

/** Ein Import, der noch nicht gebucht ist - aus SelectLine mit dem Grund, warum die Plattform ihn nicht selbst gebucht hat. */
export interface UebersichtImport {
  id: number
  dokumenttyp: string
  status: string
  dateiname: string | null
  angelegt_am: string
  quelle?: string | null
  tage?: string | null
  /** Warnungen, Rückfragen, Abweichungen vom Bestand in SelectLine - leer: unauffällig */
  gruende?: string[]
  /** Ein Bestandsvergleich, der noch auf Produktion oder Wareneingang bis zu seinem Stichtag wartet */
  wartet?: string | null
}

export interface Uebersicht {
  stichtag: string
  hinweise: OffenerHinweis[]
  lieferanten_faellig: { gruppe: string; lieferant: string; angelegt: boolean; abruf_faellig: string[]; nachauflage_faellig: string[] }[]
  importe: UebersichtImport[]
  /** Aus SelectLine ohne Klick gebucht, letzte drei Tage */
  selbst_gebucht?: { id: number; dokumenttyp: string; tage: string | null; positionen: number; gebucht_am: string }[]
  selectline_angehalten?: number
  rueckfragen: { id: number; status: string; anlass: string; frage: string; import_lauf_id: number | null }[]
  /** Rückfragen der letzten 14 Tage, die beim Buchen ohne Antwort geschlossen wurden - nicht offen, aber nachlesbar (02.11.2026) */
  rueckfragen_uebergangen?: { id: number; status: string; anlass: string; frage: string; import_lauf_id: number | null }[]
  inventuren: { id: number; stichtag: string; status: string; gezaehlt: number; materialien: number }[]
  vermerke_faellig: { id: number; faellig: string; ueberfaellig: boolean; art: string; text: string; artikelnummer: string | null; bezeichnung: string | null }[]
  zusammenfassung: string
  /** Zähler der Datenprüfung für das Abzeichen auf „Heute" - die Befunde holt `/api/datenpruefung` */
  datenpruefung?: DatenpruefungZaehler
  /** Abrufe aus SelectLine, die fehlgeschlagen sind und stehen bleiben (siehe SelectLineDienst.tsx) */
  abholung_fehler?: PickupJob[]
  /** Artikel mit Bestand in SelectLine ohne Stammsatz hier - aus dem täglichen Stammdatenabgleich */
  stammdaten_fehlend?: { artikelnummer: string; bezeichnung: string }[]
  /** Wächter, die angeschlagen haben und noch niemand gesehen hat (02.11.2026) */
  waechter_ausgeloest?: Waechter[]
  /** Was in Gesprächen mit dem Assistenten liegen geblieben ist: offene Frage, wartende Freigabe … */
  offene_faeden?: OffenerFaden[]
}

/** Ein liegen gebliebener Faden aus dem Assistenten - auf „Heute", damit am nächsten Morgen niemand raten muss. */
export interface OffenerFaden {
  /** unbeantwortet: die letzte Nachricht des Nutzers bekam keine Antwort; abbruch: der Lauf brach ab (02.11.2026) */
  art: 'frage' | 'freigabe' | 'unbeantwortet' | 'abbruch' | 'abruf' | 'import'
  text: string
  unterhaltung_id: number | null
  titel: string | null
  seit: string
  link: string | null
}

// --- Was der Assistent sich merkt (02.11.2026): Vorgaben, Wächter, Anliegen ----------------------

export type VorgabeGeltung = 'allgemein' | 'artikel' | 'lieferant'

/** Eine dauerhafte Vorgabe für den Assistenten - jedes Gespräch bekommt sie mit. */
export interface Vorgabe {
  id: number
  bereich: string
  text: string
  geltung: VorgabeGeltung
  /** Artikelnummern (Komma) oder Lieferantenname; leer bei `allgemein` */
  bezug: string | null
  angelegt_von: string | null
  angelegt_am: string
  aktiv: boolean
}

export interface VorgabeEingabe {
  text: string
  geltung: VorgabeGeltung
  bezug: string | null
  bereich?: string
}

export type WaechterArt = 'produktion_geplant' | 'bestand_unter' | 'termin'

/** Ein Auftrag „sag mir, wenn …", den die Plattform selbst prüft. */
export interface Waechter {
  id: number
  art: WaechterArt
  artikelnummer: string | null
  /** bestand_unter: Grenze in t */
  schwelle: number | null
  /** termin: an diesem Tag */
  datum: string | null
  text: string
  angelegt_von: string | null
  angelegt_am: string
  aktiv: boolean
  ausgeloest_am: string | null
  /** was gefunden wurde, in Worten */
  ausloeser: string | null
  gesehen_am: string | null
}

export type AnliegenStatus = 'offen' | 'erledigt' | 'abgelehnt'

/** Ein Wunsch oder Fehler, den der Assistent an den Entwickler weitergegeben hat. */
export interface Anliegen {
  id: number
  art: 'wunsch' | 'fehler'
  titel: string
  text: string
  bereich: string
  angelegt_von: string | null
  angelegt_am: string
  status: AnliegenStatus
  antwort: string | null
  gemailt_am: string | null
  erledigt_am: string | null
}

/** Der jüngste Stammdatenabgleich mit SelectLine (`GET /api/selectline/fehlende-artikel`). */
export interface StammdatenAbgleich {
  job_id: number | null
  stichtag: string | null
  stand: string | null
  fehlend: FehlenderArtikel[]
}

export type FaelligArt = 'mindestbestand' | 'abruf' | 'nachauflage' | 'vorstufen' | 'frist' | 'lieferung' | 'reichweite' | 'vermerk' | 'inventur' | 'stammdaten' | 'bestellabgleich'

export interface FaelligEintrag {
  tag: string
  kw: string | null
  art: FaelligArt
  titel: string
  text: string
  gruppe: string | null
  artikelnummer: string | null
  bezeichnung: string | null
  ueberfaellig: boolean
  link: string
  /** Bei `bestellabgleich`: die Bestellung, zu der der Abgleich mit SelectLine fragt. */
  bestellung_id?: number | null
  /** Welcher Eintrag - für POST /api/erledigt (Runde 4, 02.11.2026). */
  schluessel: string
  /** Sein Anlass, wie er jetzt aussieht - unverändert an POST /api/erledigt geben. */
  fingerabdruck: string
}

/** Ein Eintrag auf Heute bzw. der Fällig-Liste, von Hand erledigt oder zur Kenntnis genommen (Runde 4, 02.11.2026). */
export interface Erledigung {
  id: number
  schluessel: string
  fingerabdruck: string
  text?: string | null
  tag?: string | null
  grund?: string | null
  erledigt_von?: string | null
  erledigt_am: string
  wiedervorlage?: string | null
  aufgehoben_am?: string | null
  aufgehoben_weil?: string | null
}

export interface FaelligErledigt extends FaelligEintrag {
  erledigung: Erledigung
}

export interface Faellig {
  ab: string
  bis: string
  /** nur, was offen ist */
  eintraege: FaelligEintrag[]
  /** von Hand erledigte Einträge mit wer/wann/warum - kein Anlass nachzufragen */
  erledigt: FaelligErledigt[]
  zusammenfassung: string
}

/** Was ein freigegebener Wareneingang an Bestellungen geschlossen oder gekürzt hat. */
export interface BestellungAbgleich {
  id: number
  material: string
  bezeichnung: string
  bestellt_t: number
  geliefert_t: number
  rest_t: number
  status: string
  hinweis: string
}

// --- Postfach / Einstellungen (04.09.2026) ---

export interface MailAbsender {
  /** leer bei Einträgen aus der Serverkonfiguration - die lassen sich hier nicht löschen */
  id: number | null
  adresse: string
  name: string | null
  aktiv: boolean
  quelle: 'dashboard' | 'konfiguration'
  angelegt_am: string | null
}

/** `zuordnen`: weder Regeln noch KI wussten, in welchen Bereich die Mail gehört - ein Mensch entscheidet. */
export type MailStatus = 'verarbeitet' | 'abgewiesen' | 'ohne_inhalt' | 'fehler' | 'zuordnen'

export interface MailEingang {
  id: number
  absender: string
  /** die reine Adresse aus dem Absender, klein geschrieben */
  adresse: string
  betreff: string | null
  empfangen_am: string | null
  abgeholt_am: string | null
  status: MailStatus | null
  hinweis: string | null
  pruefung: string | null
  anhaenge: { name: string; art: 'pdf' | 'excel' | 'sonstiges' }[]
  text: string | null
  laeufe: ImportLaufKurz[]
  /** abgewiesen, und der Absender steht noch nicht auf der Liste */
  freischaltbar: boolean
  /** nicht verarbeitet, und die Mail lässt sich aus dem Postfach nachholen */
  erneut_moeglich: boolean
  /** an welche Adresse die Mail ging (zwei Postfächer seit 06.10.2026) */
  postfach?: string | null
  /** in welchen Bereich die Mail sortiert wurde; leer bei Mails vor der Umstellung */
  bereich?: BereichId | null
  /** warum dieser Bereich - „Absender cargill.com ist Notierungslieferant“, „KI: Preisliste Palmöl“ */
  bereich_grund?: string | null
  /** Läufe der Rohstoffnotierungen, die aus der Mail entstanden sind */
  notierung_laeufe?: { id: number; status: NotierungLaufStatus }[]
}

export interface PostfachStatus {
  adresse: string
  eingerichtet: boolean
  intervall_sekunden: number
  letzte_abholung: string | null
  letzter_fehler: string | null
  anzahl_zuletzt: number
  absender_gepflegt: boolean
  /** alle Postfächer einzeln (othuena@intsode.com, othuena@vai.com.de) - fehlt bei älterem Backend */
  postfaecher?: Postfach[]
}

/** Ein Postfach, aus dem abgeholt wird. */
export interface Postfach {
  adresse: string
  eingerichtet: boolean
  letzte_abholung: string | null
  letzter_fehler: string | null
  anzahl_zuletzt: number
}

// --- Eingebettete Oberfläche des Assistenten (16.09.2026) -----------------------------------------
// Der Assistent schreibt in seinen Text eingezäunte Blöcke ```ui mit einem JSON-Objekt. Die
// Oberfläche rendert daraus Tabelle, Diagramm, Formular oder Auswahl (src/komponenten/UiBlock.tsx).

export type UiZelle = string | number | boolean | null

export interface UiTabelle {
  art: 'tabelle'
  titel?: string
  spalten: string[]
  zeilen: UiZelle[][]
  /** Optionale Summenzeile, wird fett als letzte Zeile gezeigt. */
  summen?: UiZelle[]
  /** Einheit je Spaltenname, wird an Zahlen der Spalte gehängt („199,77 t"). */
  einheiten?: Record<string, string>
}

export interface UiReihe {
  name: string
  werte: (number | null)[]
}

export interface UiDiagramm {
  art: 'diagramm'
  typ: 'balken' | 'linie'
  titel?: string
  x: string[]
  reihen: UiReihe[]
  einheit?: string
}

export type UiFeldTyp = 'text' | 'zahl' | 'datum' | 'auswahl' | 'schalter'

export interface UiFeld {
  name: string
  beschriftung: string
  typ: UiFeldTyp
  optionen?: string[]
  wert?: string | number | boolean | null
  einheit?: string
  pflicht?: boolean
}

export interface UiFormular {
  art: 'formular'
  titel: string
  felder: UiFeld[]
  /** Text des Absende-Knopfs. */
  senden?: string
}

export interface UiOption {
  text: string
  /** Was als Nutzernachricht geschickt wird; fehlt es, der Text selbst. */
  antwort?: string
}

export interface UiAuswahl {
  art: 'auswahl'
  frage: string
  optionen: UiOption[]
}

export type UiBlockDaten = UiTabelle | UiDiagramm | UiFormular | UiAuswahl


// --- Anmeldung (20.09.2026) --------------------------------------------------------------------

/** Wer angemeldet ist. „verwaltung" darf zusätzlich Zugänge anlegen. */
export interface Benutzer {
  name: string
  anzeigename: string
  rolle: 'disponent' | 'verwaltung'
  aktiv: boolean
  letzte_anmeldung: string | null
  /** Die Bereiche, die dieser Benutzer öffnen darf (aufgelöst - leer gespeichert heißt im Backend „alle“).
   *  Fehlt das Feld (Backend vor dem 06.10.2026), gelten alle Bereiche. */
  bereiche?: BereichId[]
}

// --- SelectLine-Abholdienst (22.09.2026) ----------------------------------------------------------

/** Abholart am Draht - im Dashboard weiterhin deutsch beschriftet. */
export type PickupJobKind = 'production' | 'stock' | 'goods_receipt' | 'inventory' | 'info'

/** Abholstatus am Draht - im Dashboard weiterhin deutsch beschriftet. */
export type PickupJobStatus = 'open' | 'running' | 'done' | 'failed'

export interface PickupJob {
  id: number
  kind: string
  status: string
  parameters: Record<string, unknown>
  calls: Record<string, unknown>[]
  created_at: string
  created_by: string | null
  taken_at: string | null
  finished_at: string | null
  import_run_id: number | null
  api_version: string | null
  agent_version: string | null
  error: string | null
}

/** `GET /api/selectline/bestellabgleich`: der letzte Abgleich der Bestellungen mit SelectLine. */
export interface Bestellabgleich {
  job_id?: number | null
  stand?: string | null
  aenderungen?: string[]
  uebersprungen?: string[]
  /** Abrufe, die SelectLine als geliefert führt, deren Wareneingang hier noch nicht gebucht ist - je ein Satz (02.11.2026) */
  wareneingang_fehlt?: string[]
  fragen?: unknown[]
}

export interface PickupStatus {
  /** off: nichts; trial: nur Lebenszeichen, nichts wird geplant; live: Tagesplanung und alle Abholarten */
  mode: 'off' | 'trial' | 'live'
  configured: boolean
  last_contact: string | null
  agent_version: string | null
  api_version: string | null
  open: number
  running: number
  failed: number
  last_delivery: Record<string, string>
}

// --- Zwei Bereiche und Rohstoffnotierungen (06.10.2026) -----------------------------------------
// Die Typen spiegeln den API-Vertrag unter /api/notierungen. Datum ISO, Zahlen roh (EUR je Tonne).

/** Die Arbeitsbereiche der Plattform; was zu jedem gehört (Titel, Menü, Pfade), steht in bereiche.ts. */
export type BereichId = 'verpackung' | 'notierungen'

export interface NotierungLieferant {
  id: number
  name: string
  /** Komma-Liste aus Adressen oder @domänen, an denen eine Mail dieses Lieferanten erkannt wird */
  absender: string | null
  hinweis: string | null
  aktiv: boolean
  quellen_anzahl: number
}

export interface NotierungRohstoff {
  id: number
  name: string
  /** Anzeigename der Gruppe = ein Reiter der Tabelle, z. B. „Palmöl · Palmstearin“ */
  gruppe: string
  reihenfolge: number
  aktiv: boolean
}

/** Eine Spalte der Tabelle: Lieferant × Rohstoff × Qualität × Ladeort. */
export interface NotierungQuelle {
  id: number
  lieferant_id: number
  lieferant: string
  rohstoff_id: number
  rohstoff: string
  gruppe: string
  bezeichnung: string
  /**
   * die Bezeichnungen im Dokument des Lieferanten („RBD PO Food RSPO SEG low moah“) - nur lesbar, abgeleitet
   * aus den aktiven Zuordnungsregeln dieser Spalte; geändert wird über die Regeln
   */
  produkt: string[]
  qualitaet: string | null
  ladeort: string | null
  lieferort: string | null
  transport_eur: number | null
  regel: string | null
  reihenfolge: number
  aktiv: boolean
  notierungen_anzahl: number
  letzte_notierung: string | null
}

/** Was beim Anlegen und Ändern einer Spalte geschickt wird (Teiländerung); `produkt` kommt aus den Regeln. */
export type NotierungQuelleEingabe = Partial<
  Omit<NotierungQuelle, 'id' | 'lieferant' | 'rohstoff' | 'gruppe' | 'produkt' | 'notierungen_anzahl' | 'letzte_notierung'>
>

export type NotierungRegelAktion = 'spalte' | 'ignorieren'
export type NotierungRegelHerkunft = 'startdaten' | 'spalte' | 'gelernt' | 'hand' | 'assistent'

/**
 * Eine Zuordnungsregel: „Lieferant X, im Dokument steht Y [, Ladeort enthält Z] [, Qualität Q] → Spalte S“
 * oder „→ nicht übernehmen“. Die einzige Quelle der Zuordnung nach Produktname.
 */
export interface NotierungRegel {
  id: number
  lieferant_id: number
  lieferant: string
  /** was im Dokument steht - die Position passt, wenn ihr Produkt das enthält */
  produkt: string
  ladeort: string | null
  qualitaet: string | null
  aktion: NotierungRegelAktion
  quelle_id: number | null
  /** Titel der Spalte */
  quelle: string | null
  rohstoff: string | null
  grund: string | null
  herkunft: NotierungRegelHerkunft
  aktiv: boolean
  angelegt_am: string
  angelegt_von: string | null
  /** wie oft die Regel gegriffen hat */
  treffer: number
  zuletzt_am: string | null
}

/** Was beim Anlegen (POST) und Ändern (PUT, Teiländerung, ohne herkunft) einer Regel geschickt wird. */
export type NotierungRegelEingabe = Partial<
  Pick<NotierungRegel, 'lieferant_id' | 'produkt' | 'ladeort' | 'qualitaet' | 'aktion' | 'quelle_id' | 'grund' | 'aktiv' | 'herkunft'>
>

/** Antwort auf Anlegen und Ändern: die Regel und die wartenden Läufe, die danach neu zugeordnet wurden. */
export interface NotierungRegelAntwort extends NotierungRegel {
  neu_bewertet: number[]
}

export interface NotierungEinstellungen {
  /** Tagesrückgang, ab dem ein Einkaufssignal gilt (EUR/t) */
  signal_eur: number
  /** Tagesrückgang, ab dem das Signal „stark“ ist */
  stark_eur: number
  /** über so viele Notierungstage wird der Trend gerechnet */
  trend_tage: number
  /** so viel muss der Preis über diese Tage zusammen fallen */
  trend_eur: number
  /** ein größerer Sprung zum letzten Wert (Prozent) verhindert die automatische Übernahme */
  sprung_prozent: number
}

export interface NotierungGruppe {
  gruppe: string
  rohstoffe: { id: number; name: string }[]
  quellen_anzahl: number
  letzter_tag: string | null
}

export type NotierungStatus = 'preis' | 'ausverkauft' | 'auf_anfrage'
export type NotierungSignal = 'signal' | 'stark' | 'trend' | 'anstieg'

export interface NotierungZelle {
  preis: number | null
  status: NotierungStatus
  /** preis + Transportzuschlag der Spalte, ohne Zuschlag der Preis selbst */
  preis_inkl: number | null
  vortag_preis: number | null
  delta: number | null
  signal: NotierungSignal | null
  notiert_am: string
}

export interface NotierungSpalte {
  quelle_id: number
  bezeichnung: string
  lieferant: string
  rohstoff_id: number
  rohstoff: string
  qualitaet: string | null
  ladeort: string | null
  lieferort: string | null
  transport_eur: number | null
  regel: string | null
  /** Tag des gezeigten Werts (≤ Stichtag) - älter als der Stichtag heißt: an dem Tag kam nichts */
  notiert_am: string | null
  vortag: string | null
}

export interface NotierungPreisspanne {
  rohstoff_id: number
  rohstoff: string
  jahr: number
  von: number | null
  bis: number | null
  bemerkung: string | null
  quelle: 'excel' | 'berechnet'
}

export interface NotierungTabelle {
  gruppe: string
  am: string
  /** alle Tage mit Notierungen der Gruppe, absteigend - für die Stichtag-Navigation */
  tage: string[]
  rohstoffe: { id: number; name: string; quellen: number[] }[]
  spalten: NotierungSpalte[]
  /** Liefermonate als Monatserste, lückenlos, höchstens 24 */
  monate: string[]
  zellen: Record<string, Record<string, NotierungZelle>>
  /** Schlüssel „rohstoff_id|qualitaet“ (Qualität leer = ''), darunter je Liefermonat die günstigste Spalte */
  bestpreis: Record<string, Record<string, { quelle_id: number; preis_inkl: number }>>
  preisspannen: NotierungPreisspanne[]
}

/** Ein Lieferzeitraum: Monat „2026-11“, Quartal „2026-Q4“, Halbjahr „2026-H2“ oder Jahr „2026“. */
export interface NotierungZeitraum {
  schluessel: string
  art: 'monat' | 'quartal' | 'halbjahr' | 'jahr'
  text: string
  monate: string[]
}

export interface NotierungVerlauf {
  rohstoff: string
  /** Reiter der Tabelle, in dem der Rohstoff steht - Ziel des Klicks auf einen Lieferanten */
  gruppe: string
  zeitraum: NotierungZeitraum
  liefermonat: string
  x: string[]
  reihen: {
    quelle_id: number
    name: string
    qualitaet: string | null
    lieferant: string | null
    /** je Notierungstag der Preis; über mehrere Monate der Mittelwert der notierten Monate */
    werte: (number | null)[]
    /** je Notierungstag, aus wie vielen Liefermonaten gemittelt */
    monate: (number | null)[]
  }[]
  /** alle Zeiträume mit Preisen für diesen Rohstoff */
  zeitraeume: NotierungZeitraum[]
}

export interface NotierungHeuteSignal {
  quelle_id: number
  bezeichnung: string
  rohstoff: string
  liefermonat: string
  preis: number | null
  preis_inkl: number | null
  vortag_preis: number | null
  delta: number | null
  art: Exclude<NotierungSignal, 'anstieg'>
  text: string
}

export interface NotierungHeuteBestpreis {
  rohstoff: string
  qualitaet: string | null
  liefermonat: string
  quelle_id: number
  bezeichnung: string
  preis_inkl: number
  zweiter: { bezeichnung: string; preis_inkl: number } | null
}

export interface NotierungenHeute {
  am: string
  /** ein Satz, regelbasiert - steht oben auf „Heute“ und auf der Kachel der Bereichsauswahl */
  zusammenfassung: string
  eingegangen: { lieferant: string; quellen: string[]; notiert_am: string; lauf_id: number | null; uhrzeit: string | null }[]
  /** Lieferanten, die sonst regelmäßig notieren und heute noch nicht */
  ausstehend: { lieferant: string; sonst_um: string | null }[]
  signale: NotierungHeuteSignal[]
  bestpreise: NotierungHeuteBestpreis[]
  laeufe_pruefen: {
    id: number
    dateiname: string | null
    lieferant_text: string | null
    angelegt_am: string
    grund: string | null
    gruende?: string[]
  }[]
  /** angebotene Produkte ohne Spalte, die keine Regel ausschließt - Spalte anlegen oder „nicht gebraucht“ merken */
  neue_angebote?: NotierungNeuesAngebot[]
}

export interface NotierungNeuesAngebot {
  lauf_id: number
  notiert_am: string | null
  lieferant_id: number
  lieferant: string | null
  produkt: string
  qualitaet: string | null
  ladeort: string | null
  /** Rohstoff aus dem Katalog nach Lesart der KI */
  rohstoff: string | null
  /** Liefermonate mit Preis */
  monate: number
}

export type NotierungLaufStatus = 'pruefen' | 'uebernommen' | 'verworfen' | 'zurueckgenommen' | 'fehler'
export type NotierungLaufQuelle = 'mail' | 'upload' | 'hand' | 'excel' | 'text'

export interface NotierungLaufKurz {
  id: number
  quelle: NotierungLaufQuelle
  status: NotierungLaufStatus
  /** ohne Klick übernommen - alle Bedingungen der automatischen Übernahme waren erfüllt */
  automatisch: boolean
  dateiname: string | null
  notiert_am: string | null
  lieferant_text: string | null
  angelegt_am: string
  entschieden_von: string | null
  werte_anzahl: number
  offen_anzahl: number
  hinweise: string[]
}

export interface NotierungPeriode {
  liefermonat: string
  preis: number | null
  status: NotierungStatus
  /** vorhandener Wert derselben Spalte am selben Tag - die Übernahme ersetzt ihn */
  bisher: number | null
  vortag_preis: number | null
  delta: number | null
  warnung: string | null
}

export interface NotierungPosition {
  index: number
  lieferant: string | null
  produkt: string | null
  qualitaet: string | null
  ladeort: string | null
  lieferbedingung: string | null
  quelle_id: number | null
  quelle: string | null
  zuordnung: 'regel' | 'ki' | 'hand' | null
  /** die Regel, die gegriffen hat */
  regel_id: number | null
  uebernehmen: boolean
  /** warum nicht übernommen oder warum diese Spalte */
  grund: string | null
  perioden: NotierungPeriode[]
}

export interface NotierungLauf extends NotierungLaufKurz {
  mail: { absender: string; betreff: string | null; text: string | null } | null
  dateien: string[]
  positionen: NotierungPosition[]
  quellen_zur_auswahl: { id: number; bezeichnung: string; rohstoff: string; lieferant: string }[]
}

/** Antwort auf das Einlesen einer der drei alten Excel-Mappen. */
export interface NotierungExcelAntwort {
  lauf_id: number
  blaetter: number
  werte: number
  quellen_neu: number
  preisspannen: number
}
